/**
 * Rateio por colaborador (ADR 0094): transforma pessoas em parcelas por centro de custo, pelos
 * centros que cada uma tem em Dados Mestres. O resultado entra na peça comum como quantidade, sem
 * um segundo cálculo de rateio. A proporção de cada pessoa é a parte dela; sem escolha, uma por
 * colaborador (linear).
 */
import { z } from 'zod';
import { fromScaled, toScaled } from './money.ts';

export const EMPLOYEE_SPLIT_SCOPES = ['company', 'group', 'selected'] as const;
export type EmployeeSplitScope = (typeof EMPLOYEE_SPLIT_SCOPES)[number];

export const EMPLOYEE_SPLIT_METHODS = ['percent', 'amount'] as const;
export type EmployeeSplitMethod = (typeof EMPLOYEE_SPLIT_METHODS)[number];

const DECIMAL_RE = /^\d+(\.\d{1,4})?$/;
const HUNDRED = toScaled('100');

export const employeeSplitInput = z
  .object({
    scope: z.enum(EMPLOYEE_SPLIT_SCOPES),
    method: z.enum(EMPLOYEE_SPLIT_METHODS).default('percent'),
    /** Só em "selected": quem entra e a parte de cada um. */
    employees: z
      .array(z.object({ employeeId: z.uuid(), share: z.string().trim().regex(DECIMAL_RE) }))
      .max(2000)
      .default([]),
  })
  .refine((v) => v.scope !== 'selected' || v.employees.length > 0, {
    message: 'Marque pelo menos um colaborador',
    path: ['employees'],
  })
  .refine(
    (v) =>
      v.scope !== 'selected' ||
      v.method !== 'percent' ||
      // Parte ainda vazia ou inválida não quebra a conta: o campo já recusa por conta própria.
      v.employees.reduce(
        (sum, e) => sum + (DECIMAL_RE.test(e.share) ? toScaled(e.share) : 0n),
        0n,
      ) === HUNDRED,
    { message: 'Os percentuais precisam somar 100%', path: ['employees'] },
  );
export type EmployeeSplitInput = z.infer<typeof employeeSplitInput>;

/** Colaborador ativo na data, com os centros dele em Dados Mestres. */
export interface SplitEmployee {
  employeeId: string;
  legalEntityId: string | null;
  name: string;
  centers: { costCenterId: string; percentage: string }[];
}

export interface EmployeeSplitResult {
  /** Parcelas por centro, para a peça comum (método quantidade). */
  lines: { costCenterId: string; share: string }[];
  /** Quantas pessoas entraram no cálculo. */
  counted: number;
  /** Quem deveria entrar, mas não tem centro válido: aparece com alerta. */
  withoutCenter: string[];
}

/** Junta as linhas da consulta (uma por pessoa e centro) num colaborador com os centros dele. */
export function groupSplitEmployees(
  rows: readonly {
    employeeId: string;
    legalEntityId: string | null;
    name: string;
    costCenterId: string | null;
    percentage: string | null;
  }[],
): SplitEmployee[] {
  const byId = new Map<string, SplitEmployee>();
  for (const r of rows) {
    const person = byId.get(r.employeeId) ?? {
      employeeId: r.employeeId,
      legalEntityId: r.legalEntityId,
      name: r.name,
      centers: [],
    };
    if (r.costCenterId && r.percentage) {
      person.centers.push({ costCenterId: r.costCenterId, percentage: r.percentage });
    }
    byId.set(r.employeeId, person);
  }
  return [...byId.values()];
}

/**
 * Parcelas por centro do rateio por colaborador. Centros inválidos são ignorados; quem fica sem
 * nenhum centro válido sai do cálculo e vai para `withoutCenter`.
 */
export function employeeSplitLines(
  input: Pick<EmployeeSplitInput, 'scope' | 'employees'>,
  people: readonly SplitEmployee[],
  legalEntityId: string,
  isValidCenter: (costCenterId: string) => boolean = () => true,
): EmployeeSplitResult {
  const byId = new Map(people.map((p) => [p.employeeId, p]));
  const chosen: { person: SplitEmployee; share: string }[] =
    input.scope === 'selected'
      ? input.employees.flatMap((e) => {
          const person = byId.get(e.employeeId);
          return person ? [{ person, share: e.share }] : [];
        })
      : people
          .filter((p) => input.scope === 'group' || p.legalEntityId === legalEntityId)
          .map((person) => ({ person, share: '1' }));
  const weights = new Map<string, bigint>();
  const withoutCenter: string[] = [];
  let counted = 0;
  for (const { person, share } of chosen) {
    const centers = person.centers.filter((c) => isValidCenter(c.costCenterId));
    if (!centers.length) {
      withoutCenter.push(person.name);
      continue;
    }
    counted += 1;
    for (const c of centers) {
      // Parte da pessoa × percentual dela no centro; a escala maior evita perder parte pequena.
      const weight = (toScaled(share) * toScaled(c.percentage)) / HUNDRED;
      weights.set(
        c.costCenterId,
        (weights.get(c.costCenterId) ?? 0n) + (weight > 0n ? weight : 1n),
      );
    }
  }
  const lines = [...weights]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([costCenterId, weight]) => ({ costCenterId, share: fromScaled(weight) }));
  return { lines, counted, withoutCenter };
}
